import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

/**
 * ProtectedRoute - Route guard component that checks authentication and optionally role
 *
 * Usage:
 *   <Route element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
 *   <Route element={<ProtectedRoute requiredRoles={['admin', 'manager']}><AdminPage /></ProtectedRoute>} />
 *   <Route element={<ProtectedRoute requiredPermissions={['fraud.alerts.view']}><FraudPage /></ProtectedRoute>} />
 *
 * requiredRoles reads users.role by name. requiredPermissions (any of) reads the codes
 * GET /api/auth/me returned, through AuthContext.hasAnyPermission (an admin holds every code).
 * Given both, either one admits, as the server's requireRoleOrPermission does (F2, 2026-10-27).
 */
const ProtectedRoute = ({ children, requiredRoles = [], requiredPermissions = [] }) => {
  const { isAuthenticated, loading, user, hasAnyPermission } = useAuth();
  const location = useLocation();

  // Show loading state while checking auth
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: '#f9fafb'
      }}>
        <div style={{
          textAlign: 'center'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e5e7eb',
            borderTopColor: '#667eea',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    // Save the current location so we can redirect back after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role / permission requirements if specified: either one admits.
  const gated = requiredRoles.length > 0 || requiredPermissions.length > 0;
  if (gated && user) {
    const userRole = user.role?.toLowerCase();
    const hasRequiredRole = requiredRoles.some(
      role => role.toLowerCase() === userRole
    );
    const hasRequiredPermission = requiredPermissions.length > 0
      && typeof hasAnyPermission === 'function' && hasAnyPermission(requiredPermissions);

    if (!hasRequiredRole && !hasRequiredPermission) {
      // User doesn't have required role - show access denied
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 140px)',
          background: '#f9fafb',
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '48px',
            textAlign: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            maxWidth: '400px'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              background: '#fef2f2',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '28px'
            }}>
              <span role="img" aria-label="locked">🔒</span>
            </div>
            <h2 style={{
              margin: '0 0 12px 0',
              fontSize: '20px',
              fontWeight: '600',
              color: '#111827'
            }}>
              Access Denied
            </h2>
            <p style={{
              margin: '0 0 24px 0',
              fontSize: '14px',
              color: '#6b7280',
              lineHeight: '1.5'
            }}>
              You don't have permission to access this page.
              Contact your administrator if you believe this is an error.
            </p>
            <p style={{
              margin: 0,
              fontSize: '12px',
              color: '#9ca3af'
            }}>
              {requiredRoles.length > 0 && <span style={{ display: 'block' }}>Required role: {requiredRoles.join(' or ')}</span>}
              {requiredPermissions.length > 0 && (
                <span style={{ display: 'block' }}>Required permission: {requiredPermissions.join(' or ')}</span>
              )}
            </p>
          </div>
        </div>
      );
    }
  }

  // Authenticated and has required role - render children
  return children;
};

export default ProtectedRoute;
